'use client';

import AppStorage, { getErrorMessge } from '@/utils/helpers';
import axiosInstance from '@/utils/https';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from '@/redux/hooks';
// import { useRouter } from 'next/navigation';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import Calendar from '../Calendar';
import format from 'date-fns/format';

interface Props {
  fetchLoyaltyUser: (telephone: string) => void;
}

function LoyaltyForm({ fetchLoyaltyUser }: Props) {
  const { user } = useAppSelector((state: any) => state.auth);
  const amount = AppStorage.get('amountSpent') as string;
  const orderId = AppStorage.get('latestOrderId') as string;
  const [agree, setAgree] = useState<boolean>(false);
  const [canActivate, setCanActivate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    date_of_birth: '',
    email: '',
    orderCode: '',
  });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    const allFilled = Object.entries(formData)
      .filter(([key]) => key !== 'email' && key !== 'orderCode')
      .every(([, val]) => val.trim() !== '');
    setCanActivate(allFilled && agree);
  }, [formData, agree]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = format(date, 'dd, MMMM');
      console.log('Date: ', formattedDate);
      setFormData((prev) => ({ ...prev, date_of_birth: formattedDate }));
    }
  };

  const convertDate = () => {
    const [dayStr, monthName] = formData.date_of_birth
      .replace(',', '')
      .split(' ');
    const day = parseInt(dayStr, 10);
    const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
    return new Date(2000, monthIndex, day);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const requiredFields = ['firstName', 'lastName', 'gender', 'date_of_birth'];
    const errors: Record<string, string> = {};

    requiredFields.forEach((field) => {
      if (!formData[field as keyof typeof formData]?.trim()) {
        errors[field] = 'This field is required';
      }
    });

    setFormErrors(errors);

    if (!canActivate) {
      toast.error('Please fill in all required fields');
      return;
    }
    const telephone = user?.telephone;
    const amountSpent = parseFloat(amount ?? '0');
    const form = {
      ...formData,
      telephone,
      amountSpent,
      orderId,
    };

    try {
      setLoading(true);
      const url = `/api/v1/loyalty`;
      await axiosInstance.post(url, form);
      setLoading(false);
      toast.success('Loyalty program activated successfully');
      AppStorage.remove('amountSpent');
      AppStorage.remove('latestOrderId');
      AppStorage.remove('inForm');
      fetchLoyaltyUser(user.telephone);
      // setActivePage('loyalty');
    } catch (error) {
      console.log(error);
      const msg = getErrorMessge(error);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen py-2">
      <div className="w-full flex flex-col justify-between py-4">
        <div className="flex flex-col gap-2 mb-6">
          <h2 className="text-2xl font-semibold text-[#100A37]">
            Loyalty Program
          </h2>
          <p className="text-[#475569]">
            Fill in the right details here to get started
          </p>
        </div>

        <form
          className="w-full flex flex-col flex-1 justify-between gap-8 sm:gap-20 mb-4"
          onSubmit={handleSubmit}
        >
          <div className="space-y-4">
            <div>
              <label className="block font-medium text-[#475569]">
                First name <span className="text-crunchies-red mr-2">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={handleChange}
                className={`mt-1 w-full border rounded-lg py-3 px-4 text-sm ${
                  formErrors.firstName ? 'border-red-500' : ''
                }`}
              />
              {formErrors.firstName && (
                <p className="text-sm text-red-500 mt-1">
                  {formErrors.firstName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569]">
                Last name <span className="text-crunchies-red mr-2">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={handleChange}
                className={`mt-1 w-full border rounded-lg py-3 px-4 text-sm ${
                  formErrors.lastName ? 'border-red-500' : ''
                }`}
              />
              {formErrors.lastName && (
                <p className="text-sm text-red-500 mt-1">
                  {formErrors.lastName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569]">
                Gender <span className="text-crunchies-red mr-2">*</span>
              </label>
              <Listbox
                value={formData.gender}
                onChange={(value) =>
                  setFormData({ ...formData, gender: value })
                }
              >
                <div className="relative">
                  <Listbox.Button
                    className={`w-full py-3 px-4 border rounded-lg text-sm text-left cursor-default flex justify-between shadow-sm ${formErrors.gender ? 'border-red-500' : ''}`}
                  >
                    {formData.gender || 'Select gender'}
                    <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                  </Listbox.Button>
                  <Listbox.Options className="absolute z-10 right-0 mt-1 w-1/2 bg-white shadow-lg rounded-lg text-sm">
                    {['Male', 'Female', 'Prefer not to say'].map((gender) => (
                      <Listbox.Option
                        key={gender}
                        value={gender}
                        className={({ active }) =>
                          `cursor-pointer px-4 py-2 rounded-lg ${
                            active
                              ? 'bg-crunchies-red text-white'
                              : 'text-gray-700'
                          }`
                        }
                      >
                        {gender}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
              {formErrors.gender && (
                <p className="text-sm text-red-500 mt-1">{formErrors.gender}</p>
              )}
            </div>

            <div className="w-full relative">
              <label className="block text-sm font-medium text-[#475569]">
                Date of birth <span className="text-crunchies-red mr-2">*</span>
              </label>

              <Calendar
                selectedDate={formData.date_of_birth ? convertDate() : null}
                onChange={handleDateChange}
                minDateString="1950-01-01"
                maxDateString="2020-12-31"
                dateFormat="MMM dd, yyyy"
                showYearDropdown={true}
                showMonthDropdown={true}
                scrollableYearDropdown={true}
                dayMonthOnly={true}
              />
              {formErrors.date_of_birth && (
                <p className="text-sm text-red-500 mt-1">
                  {formErrors.date_of_birth}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569]">
                Email (Optional)
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg py-3 px-4 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#475569]">
                Outlet code (optional)
              </label>
              <input
                type="text"
                placeholder="Enter outlet code"
                name="orderCode"
                value={formData.orderCode}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg py-3 px-4 text-sm"
              />
            </div>

            <div className="flex items-center text-sm">
              <input
                type="checkbox"
                id="agree"
                checked={agree}
                onChange={() => setAgree(!agree)}
                className="mr-2"
              />
              <label
                htmlFor="agree"
                className="text-[#475569] text-xs font-normal"
              >
                I have read{' '}
                <span className="text-black font-bold cursor-pointer">
                  Terms & Conditions
                </span>{' '}
                before activating
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={!agree}
            className={`w-full mt-4 py-4 px-6 rounded-lg text-white font-medium ${
              agree
                ? 'bg-crunchies-red hover:bg-crunchies-red/80'
                : 'bg-[#B0B0B033] cursor-not-allowed text-[#ABABAB]'
            } ${loading ? 'opacity-40 cursor-not-allowed' : ''} `}
          >
            {loading ? 'Submitting...' : 'Activate program'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoyaltyForm;
